import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, FileText, CheckCircle, AlertTriangle, Download } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

const TermsAndConditionsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  const handleAgree = async () => {
    if (!agreed) {
      toast.error('Please check the box to confirm you have read and agree to the terms');
      return;
    }
    if (!signature.trim()) {
      toast.error('Please enter your full name as your signature');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/standin/agree-terms`, {
        signature: signature.trim(),
        agreed_at: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Terms accepted successfully');
      navigate('/standin');
    } catch (error) {
      toast.error('Failed to save agreement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-16 w-auto"
            />
          </Link>
          <Link to="/standin" className="text-white/60 hover:text-white flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12" ref={containerRef}>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 mb-4">
            <FileText className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">
            Terms & Conditions of Engagement
          </h1>
          <p className="text-white/60">
            Please read carefully before agreeing
          </p>
        </div>

        {/* Terms Content */}
        <div className="bg-[#121212] border border-white/5 rounded-2xl p-8 mb-8 max-h-[60vh] overflow-y-auto">
          <div className="prose prose-invert prose-sm max-w-none">
            <h2 className="text-[#D4AF37] text-xl font-bold mb-4">The 2nd Team Terms and Conditions of Engagement</h2>
            
            <h3 className="text-white font-semibold mt-6 mb-3">Your contract with us:</h3>
            <ol className="text-white/80 space-y-2 list-decimal pl-5">
              <li>Please carefully review the terms and conditions below as they form the binding contract between you and 2nd Team Casting. We are located at Unit 121927, PO Box 6945, London, W1A 6US.</li>
              <li>By submitting your application to be a registered performer with 2nd Team Casting, you agree to be bound by this contract. If you have any questions, please contact us.</li>
              <li>We may periodically update the guide to reflect changes in the requirements of production companies. Please make sure to check it regularly.</li>
            </ol>

            <h3 className="text-white font-semibold mt-6 mb-3">How to apply:</h3>
            <p className="text-white/80">4. To be included in our casting book of performers, please submit the online application form. It is important to provide complete and accurate information, including photographs taken within the last 6 months.</p>
            
            <p className="text-white/80 mt-4">5. Important: By submitting your application, you agree to the following:</p>
            <ul className="text-white/80 space-y-2 list-disc pl-5">
              <li>You are bound by this contract and will comply with instructions provided in the guide or by any member of 2nd Team Casting's staff.</li>
              <li>You do not have any unspent criminal convictions, except for driving convictions, including sexual offences under the Sexual Offences Act 2003.</li>
              <li>You are not listed on the sex offenders register and have never faced disciplinary action or sanction related to vulnerable groups.</li>
              <li>You will cooperate with any request from a production company to obtain a criminal record check.</li>
              <li>You are not knowingly allergic to any make-up or prosthetics, or you have informed us in writing.</li>
              <li>You confirm that there are no material facts that should be disclosed and could be relevant to your engagement, or likely to cause illness, injury, or damage to yourself or others.</li>
              <li>You confirm that you are legally entitled to live and work in the UK.</li>
              <li>You agree that we may publish your details (including photographs) in the casting book, on our website, and in other materials. We may also forward your details to production companies or other clients of 2nd Team Casting.</li>
            </ul>

            <p className="text-white/80 mt-4">6. You will become officially registered with 2nd Team Casting upon receiving an approval email from us.</p>
            <p className="text-white/80 mt-2">7. Important: Submitting an application does not guarantee automatic inclusion in our casting book. We reserve the right to refuse your application or remove you from our casting book for any reason.</p>
            <p className="text-white/80 mt-2">8. Once you receive the approval email, you must upload a copy of your passport photo page or proof of right to work in the UK (if applicable) through our website.</p>
            <p className="text-white/80 mt-2">9. Upon approval, you will be included in our casting book for 12 months from the approval date. This contract will automatically expire on that date.</p>
            <p className="text-white/80 mt-2">10. Important: Upon acceptance of your application, you appoint 2nd Team Casting as your agent to find you bookings as a Stand-in, Double or walk-on artist, or similar work with production companies. Please note that you are not engaged as an employee or contractor of 2nd Team Casting under any circumstances.</p>
            <p className="text-white/80 mt-2">11. Important: While we strive to secure bookings for all artists in our casting book, we cannot guarantee offering you any bookings at any given time.</p>

            <h3 className="text-white font-semibold mt-6 mb-3">Your details and data privacy:</h3>
            <p className="text-white/80">12. It is your responsibility to provide accurate details in your application and keep them updated. If we find any materially inaccurate details, we reserve the right to remove you from the casting book and terminate this contract.</p>
            <p className="text-white/80 mt-2">13. Specifically, you must ensure the accuracy and currency of your physical descriptions, measurements, and photographs reflecting your current appearance. Failure to do so may result in being sent home from a booking without payment.</p>
            <p className="text-white/80 mt-2">14. You must also ensure the correctness and currency of your bank account details, as we use this account for fee payments via BACS.</p>
            <p className="text-white/80 mt-2">15. If we do not accept you into our casting book, we will delete your data within one month.</p>
            <p className="text-white/80 mt-2">16. According to the Data Protection Act (1998) and GDPR (2018), we act as the data controller for all the details we hold about you. We will only use your information for purposes related to this contract or as agreed by you or permitted by law.</p>

            <h3 className="text-white font-semibold mt-6 mb-3">Our fees:</h3>
            <p className="text-white/80">19. An induction fee of £250 (inclusive of VAT) will be deducted from the first paid filming day booking arranged for you, during your 12-month registration. This fee covers administrative costs and entry into the casting book, and it is non-refundable. An annual administration fee of £50 will be deducted each year to remain on our books.</p>
            <p className="text-white/80 mt-2">20. Additionally, we deduct a 12.5% (plus VAT) commission from all fees you receive for bookings we arrange on your behalf, which is referred to as "our commission."</p>

            <h3 className="text-white font-semibold mt-6 mb-3">Bookings:</h3>
            <p className="text-white/80">21. Periodically, we may contact you to offer you bookings. However, we are not obligated to provide you with any bookings at any given time.</p>
            <p className="text-white/80 mt-2">22. You have the freedom to accept or decline any booking offered to you. If you accept a booking, you must reserve the entire day for it, even if it only involves a partial day commitment, such as a costume fitting.</p>
            <p className="text-white/80 mt-2">23. In the event that you become unavailable for a booking you have accepted, it is crucial to promptly notify the individual at 2nd Team Casting who booked you. Failure to show up for a booking without a valid reason may result in your removal from our casting book.</p>
            <p className="text-white/80 mt-2">24. Important: Production companies have the right to cancel a booking without incurring charges until noon on the day prior to the booking. We will notify you as soon as possible after receiving notice of a cancellation.</p>
            <p className="text-white/80 mt-2">25. You must adhere to the instructions outlined in the "Being on set" section of the website and have thoroughly read and understood all the Artiste FAQs.</p>
            <p className="text-white/80 mt-2">26. Important: Once you have accepted a booking, it is essential to reconfirm your availability to us via email, text, or phone before 5 PM on the day preceding the booking. Failure to do so grants us the right to replace you without further notice.</p>

            <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-4 mt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[#F59E0B] font-semibold">Important: Two Strikes Policy</p>
                  <p className="text-white/70 text-sm mt-1">Non-attendance and tardiness can severely damage our professional reputation. Therefore, we enforce a strict "two strikes" policy. If a Performer fails to attend one booking within a 12-month period, they will receive a written warning. If the Performer then fails to attend a second booking during that same period, they will be automatically removed from our roster.</p>
                </div>
              </div>
            </div>

            <h3 className="text-white font-semibold mt-6 mb-3">Your engagement:</h3>
            <p className="text-white/80">27. Your participation in any booking is subject to a separate contract with the production company. The guide contains a standard version of the production company agreement, which you are bound by.</p>
            <p className="text-white/80 mt-2">28. Some production companies may make alterations to this agreement, and the relevant version will be provided to you via email from 2nd Team Casting when booking you for each engagement.</p>
            <p className="text-white/80 mt-2">31. Prompt arrival at the production location by the given call time is mandatory. If you arrive late, the production may refuse your entry to the set, and you will not receive payment.</p>
            <p className="text-white/80 mt-2">32. While on set (or during any transportation to set provided by the production company), you must:</p>
            <ul className="text-white/80 space-y-1 list-disc pl-5">
              <li>Maintain a professional and courteous demeanor at all times.</li>
              <li>Refrain from making any film or sound recordings or taking photographs.</li>
              <li>Adhere to any rules and regulations set by the production company and follow all instructions given to you.</li>
            </ul>

            <h3 className="text-white font-semibold mt-6 mb-3">Confidentiality:</h3>
            <p className="text-white/80">36. Important: The work you undertake on any booking is strictly confidential, and you must not disclose any details to anyone. For example:</p>
            <ul className="text-white/80 space-y-1 list-disc pl-5">
              <li>Making any film or sound recordings or taking photographs while on set is prohibited.</li>
              <li>Contacting any journalists, newspapers, magazines, or other publications about any production you have participated in is strictly forbidden.</li>
            </ul>
            <p className="text-white/80 mt-2">Posting any comments or photographs on websites, blogs, or social networks regarding any production you have been involved in (including Facebook, Twitter, Instagram, Snapchat, WhatsApp, TikTok, or any other social media platform) is not allowed.</p>

            <h3 className="text-white font-semibold mt-6 mb-3">Payments:</h3>
            <p className="text-white/80">38. You authorise 2nd Team Casting to receive all fees from production companies for your bookings on your behalf. These fees will be deposited into our client account.</p>
            <p className="text-white/80 mt-2">39. Within ten working days of receiving your fees (typically six weeks after completing the booking, but potentially longer depending on the production company), we will deduct our 12.5% commission (plus VAT) and then transfer the remaining balance directly into your bank account via BACS.</p>
            <p className="text-white/80 mt-2">40. Payment from a production company cannot be passed on to you by 2nd Team Casting until we have received payment from the production company.</p>
            <p className="text-white/80 mt-2">42. On certain TV and film productions, you will be provided with a salary voucher (chit) at the start of each filming day. It is your responsibility to retain this voucher, ensure it is signed at the end of each day by an appropriate representative of the production company, and return it to the production company.</p>

            <h3 className="text-white font-semibold mt-6 mb-3">Tax Status:</h3>
            <p className="text-white/80">43. For taxation purposes, you are classified as self-employed. Consequently, you are responsible for paying your own income tax and completing your annual tax return.</p>
            <p className="text-white/80 mt-2">44. Your status for National Insurance purposes is self-employed. No Class 1 National Insurance deductions will be made from your payment, and you will receive payments without National Insurance deductions.</p>

            <h3 className="text-white font-semibold mt-6 mb-3">Termination:</h3>
            <p className="text-white/80">45. This contract will automatically expire 12 months from the approval date. We may contact you prior to that date to discuss renewal.</p>
            <p className="text-white/80 mt-2">47. You have the right to terminate this contract at any time by providing us with written notice of 30 days. Either party may terminate this contract by serving written notice on the other party if the other party is in material or persistent breach of this contract.</p>
            <p className="text-white/80 mt-2">48. Upon expiry or termination, we will promptly remove you from our casting book. Any fees received from production companies (after deducting our commission) for bookings you attended prior to termination will continue to be paid to you.</p>

            <h3 className="text-white font-semibold mt-6 mb-3">General:</h3>
            <p className="text-white/80">54. This contract does not create an employment, partnership, or joint venture relationship between you and us, nor does it authorise you to act as our agent.</p>
            <p className="text-white/80 mt-2">57. This contract represents the entire agreement between you and us, superseding all previous verbal or written agreements.</p>
            <p className="text-white/80 mt-2">58. This contract, its interpretation, enforcement, and any other matters or disputes arising from it, shall be governed by English law. Both parties submit to the exclusive jurisdiction of the English courts.</p>
          </div>
        </div>

        {/* Agreement Section */}
        <div className="bg-[#121212] border border-white/5 rounded-2xl p-8">
          <h3 className="font-['Playfair_Display'] text-xl font-bold text-white mb-6">Agreement</h3>
          
          <div className="space-y-6">
            {/* Checkbox */}
            <label className="flex items-start gap-4 cursor-pointer p-4 bg-[#0A0A0A] rounded-lg border border-white/5 hover:border-[#D4AF37]/30 transition-colors">
              <Checkbox
                checked={agreed}
                onCheckedChange={setAgreed}
                className="mt-1 w-6 h-6 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                data-testid="agree-checkbox"
              />
              <span className="text-white/80 text-sm leading-relaxed">
                I confirm that I have read, understood, and agree to be bound by the 2nd Team Terms and Conditions of Engagement. I understand that this is a legally binding contract between myself and 2nd Team Casting.
              </span>
            </label>

            {/* Signature */}
            <div>
              <Label className="text-white/80 mb-2 block">Digital Signature (Type your full name) *</Label>
              <Input
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Enter your full legal name"
                className="bg-[#0A0A0A] border-white/10 text-white h-12 font-['Caveat'] text-xl"
                data-testid="signature-input"
              />
              <p className="text-white/40 text-sm mt-2">By typing your name above, you acknowledge this serves as your electronic signature.</p>
            </div>

            {/* User Info */}
            {user && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-[#0A0A0A] rounded-lg">
                <div>
                  <p className="text-white/40 text-sm">Performer Name</p>
                  <p className="text-white font-medium">{user.full_name}</p>
                </div>
                <div>
                  <p className="text-white/40 text-sm">Date</p>
                  <p className="text-white font-medium">{new Date().toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <Button
              onClick={handleAgree}
              disabled={loading || !agreed || !signature.trim()}
              className="w-full gold-gradient text-black font-bold py-4 text-lg uppercase tracking-wider hover:brightness-110 disabled:opacity-50"
              data-testid="submit-agreement-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              {loading ? 'Processing...' : 'I Agree to the Terms & Conditions'}
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-white/40 text-sm">
          © 2026 2nd Team Casting. All rights reserved. | Unit 121927, PO Box 6945, London, W1A 6US
        </div>
      </footer>
    </div>
  );
};

export default TermsAndConditionsPage;
